/**
 * Utilities for formatting activity data into human-readable text.
 */

/**
 * Section name mappings for catalog update activities.
 */
const SECTION_LABELS: Record<string, string> = {
  descriptions: 'descriptions',
  measurements: 'measurements',
  keywords: 'catalog numbers',
  additionalInfo: 'additional info',
  additional_info: 'additional info',
  provenance: 'provenance',
  exhibitions: 'exhibitions',
  items: 'items',
};

/**
 * Format a section name into human-readable text.
 */
export function formatSection(section: string): string {
  return SECTION_LABELS[section] || section;
}

/**
 * Format activity details into human-readable text based on activity type.
 *
 * @param activityType - The type of activity (CATALOG_CREATE, CATALOG_UPDATE, etc.)
 * @param details - The activity details object
 * @returns Human-readable description of the activity
 */
export function formatActivityDetails(
  activityType: string,
  details: Record<string, unknown>
): string {
  switch (activityType) {
    case 'CATALOG_CREATE':
      return 'Created new catalog entry';

    case 'CATALOG_UPDATE': {
      const section = details.section as string | undefined;
      if (section) {
        return `Updated ${formatSection(section)}`;
      }
      return 'Updated catalog';
    }

    case 'CATALOG_DELETE':
      return 'Deleted catalog entry';

    case 'CATALOG_VISIBILITY_CHANGE': {
      const newVisibility = details.newVisibility as string | undefined;
      if (newVisibility) {
        return `Changed visibility to ${newVisibility}`;
      }
      return 'Changed visibility';
    }

    case 'USER_DATA_EXPORT':
      return 'Exported user data';

    default:
      return activityType;
  }
}

/**
 * Format a timestamp into a locale-specific date/time string.
 */
export function formatTimestamp(timestamp: string): string {
  return new Date(timestamp).toLocaleString();
}

/**
 * Format aggregated count for display (e.g., "3x" or empty if count is 1).
 */
export function formatAggregatedCount(count: number): string {
  return count > 1 ? `${count}x` : '';
}
